import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What is Manikandan Sivasankar's area of expertise?",
      answer: "Manikandan specializes in Strategic Project Management, Scrum Mastery, and Agile/SAFe program leadership. With 16+ years of experience, he has driven digital transformation initiatives across Banking, Insurance, and Healthcare sectors, with expertise in cloud modernization (Azure, Salesforce), Adobe Experience Manager (AEM), and microservices architecture."
    },
    {
      question: "Which industries has Manikandan worked in?",
      answer: "Manikandan has extensive experience across multiple industries including Banking (Citi, Bank of America, M&T), Insurance (USAA), Technology (HPE, Rackspace), Healthcare (Anthem), Food Service (GFS), and Telecom (Nokia). His diverse background enables him to bring cross-industry best practices to every project."
    },
    {
      question: "What project management methodologies does Manikandan use?",
      answer: "Manikandan is proficient in multiple methodologies including Agile, Scrum, SAFe (Scaled Agile Framework), and Waterfall. He has successfully transitioned organizations from Waterfall to Agile, achieving up to 30% improvement in delivery velocity and team predictability."
    },
    {
      question: "What technical skills does Manikandan bring to projects?",
      answer: "Manikandan brings expertise in Azure cloud services, Salesforce integration, Adobe Experience Manager (AEM), API integration patterns, microservices architecture, CI/CD pipelines (Jenkins), payment systems (Zelle, Stripe), and PCI DSS/PA DSS compliance. He bridges the gap between technical teams and business stakeholders effectively."
    },
    {
      question: "How can I contact Manikandan for consulting or opportunities?",
      answer: "You can reach Manikandan via email at contact@manikandansivasankar.com, phone at 210-544-3334, or through his LinkedIn profile at linkedin.com/in/msivasankar. For project inquiries, please use the contact form on this website for a prompt response."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-gray-50" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <HelpCircle className="w-4 h-4" />
            Frequently Asked Questions
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Common Questions
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about my experience, skills, and how I can help with your projects.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-200 hover:shadow-md"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between gap-4"
                aria-expanded={openIndex === index}
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </h3>
                <span className="flex-shrink-0 text-blue-600">
                  {openIndex === index ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </span>
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-5">
                  <p className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Have a different question?
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Get in Touch
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
